//@ flow
import {
  all,
  take,
  takeEvery,
  takeLatest,
  put,
  race,
  fork,
  select,
} from "redux-saga/effects";

import { fetchRemoteData } from "@quantum/remote-data";

import {
  abrirArquivo as abrirArquivoAction,
  alertaErro,
  arquivoSalvo as arquivoSalvoAction,
  cancelaAberturaDeArquivo,
  erroAoSalvarArquivo,
  erroAoAbrirArquivo,
  i18n,
  abrirArquivoSuccess,
  adicionarMedidas,
  adicionarMedidasParaPersonalizar,
  selecionarDataBase,
  getMedidasSelecionadas,
  getMedidasSuportadasExcel,
  getMedidasParaPersonalizar,
  getDataBaseSelecionada,
  getDataFechamentoMesAnterior,
  gerarRelatorio,
  resetGridState,
  selecionarFechamentoDeMes,
  getModoApp,
  getMedidasFixas,
  getGridState,
  restoreGridState,
  getConfiguracaoGlobalRelatorio,
  selecionarPeriodo,
  selecionarDataInicial,
  selecionarExibirDatasSemNegociacaoSeries,
  selecionarIdentificadorSeries,
  selecionarPeriodicidade,
  selecionarAjusteDias,
  selecionarDataFinal,
} from "../state";
import {
  type MedidaSelecionada,
  type MedidaParaPersonalizar,
  FORMATO_DATA_BANCO,
  TIPO_ARQUIVO_EMPRESA,
  ArquivoConfiguracaoRelatorio,
  parseVisualization,
  MODO_APP_NORMAL,
  MODO_APP_SERIES,
  ID_PERIODO_DATAS_ESPECIFICAS_MODO_SERIES,
} from "../model";

import { takeFetchRemoteData } from "../util";

import { assoc, path } from "ramda";

import Moment from "moment";

export function* obterListaArquivos({ api }) {
  yield takeFetchRemoteData("arquivosMedidas", function*(action) {
    const {
      meta: { resolve, reject },
    } = action;

    try {
      const arquivos = yield api.obterListaArquivosMedidas();
      yield put(resolve(arquivos));
    } catch (error) {
      yield put(reject(error));
    }
  });
}

function* obterGruposCompartilhamento({ api }) {
  yield takeFetchRemoteData("gruposCompartilhamento", function*(action) {
    const {
      meta: { resolve, reject },
    } = action;

    try {
      const gruposCompartilhamento = yield api.obterGruposCompartilhamento();
      yield put(resolve(gruposCompartilhamento));
    } catch (erro) {
      yield put(reject(erro));
    }
  });
}

function* compartilharArquivo({ frameApi, api }) {
  yield takeEvery("COMPARTILHAR_ARQUIVO", function*(action) {
    const { translate } = yield select(i18n);
    const {
      payload: {
        arquivo,
        nomeArquivoCompartilhado,
        codigosUsuariosACompartilhar,
      },
    } = action;

    try {
      yield api.compartilharArquivo({
        arquivo,
        nomeArquivoCompartilhado,
        codigosUsuariosACompartilhar,
      });
      yield frameApi.sendNotification(
        translate("telas.compartilharArquivo.compartilhadoComSucesso", [
          nomeArquivoCompartilhado,
        ])
      );
    } catch (error) {
      yield frameApi.sendNotificationWithTemplate(
        translate("telas.compartilharArquivo.erroAoCompartilhar"),
        "erro"
      );
    }
  });
}

export function* obterListaTemplates({ api }) {
  yield takeFetchRemoteData("templatesMedidas", function*(action) {
    const {
      meta: { resolve, reject },
    } = action;

    try {
      const arquivos = yield api.obterListaTemplatesMedidas();
      yield put(resolve(arquivos));
    } catch (error) {
      yield put(reject(error));
    }
  });
}

function* abrir({ arquivo, api, frameApi }) {
  try {
    yield frameApi.salvarArquivoMedidas(arquivo);

    const arquivoConfiguracaoRelatorio: ArquivoConfiguracaoRelatorio = yield api.abrirArquivoMedidas(
      arquivo
    );
    const medidasArquivo: Array<MedidaSelecionada> =
      arquivoConfiguracaoRelatorio.medidasSelecionadas;
    const visualizacao = arquivoConfiguracaoRelatorio.configuracoesAdicionais;

    if (
      arquivo.tipoArquivo === TIPO_ARQUIVO_EMPRESA &&
      arquivoConfiguracaoRelatorio.medidasSelecionadas.length === 1
    ) {
      const { translate } = yield select(i18n);
      yield put(
        alertaErro(
          translate("configuracaoRelatorio.medidasEmpresasNaoSeAplicam")
        )
      );
    }

    const configuracaoGlobalRelatorio =
      arquivoConfiguracaoRelatorio.configuracaoGlobalRelatorio;
    const isFechamentoDeMes =
      configuracaoGlobalRelatorio.tipoDataBase === "FECHAMENTO";
    const dataBaseSelecionada = isFechamentoDeMes
      ? Moment(yield select(getDataFechamentoMesAnterior))
      : yield select(getDataBaseSelecionada);

    const dataBase = dataBaseSelecionada.format(FORMATO_DATA_BANCO);
    const medidasArquivoDataBase = medidasArquivo.map(medida =>
      "dataBase" in medida ? assoc("dataBase", dataBase, medida) : medida
    );

    const medidasSuportadas = yield select(getMedidasSuportadasExcel);
    const medidasSomenteComSuportadas = medidasSuportadas
      ? medidasArquivoDataBase.filter(({ medida }) =>
          medidasSuportadas.has(medida.idConfiguracaoRelatorio)
        )
      : medidasArquivoDataBase;

    const medidasParaPersonalizar: Array<MedidaParaPersonalizar> =
      arquivoConfiguracaoRelatorio.medidasParaPersonalizar;

    // Arquivos salvos na tela de LDD (Excel) não possuem medidas fixas, porém estas devem aparecer no modo web
    const modo = yield select(getModoApp);
    const medidasFixas = yield select(getMedidasFixas);
    const idsMedidasParaAdicionar = medidasSomenteComSuportadas.map(
      path(["medida", "idConfiguracaoRelatorio"])
    );
    const medidasFixasAAdicionar = medidasFixas.filter(
      ({ medida: { idConfiguracaoRelatorio: id } }) =>
        !idsMedidasParaAdicionar.includes(id)
    );
    const medidasParaAdicionar =
      modo === MODO_APP_NORMAL || modo === MODO_APP_SERIES
        ? [...medidasFixasAAdicionar, ...medidasSomenteComSuportadas]
        : medidasSomenteComSuportadas;

    const SUBSTIRUIR_SELECAO = true;
    yield put(adicionarMedidas(medidasParaAdicionar, SUBSTIRUIR_SELECAO));
    yield put(adicionarMedidasParaPersonalizar(medidasParaPersonalizar));
    // Após abrir um arquivo é necessário atualizar as descrições de todas as medidas.
    // Ao fazer um seleção de "data base" (pelo mesmo valor já selecionado) as descrições
    // são calculadas novamente.
    yield put(selecionarDataBase(dataBaseSelecionada));
    yield put(selecionarFechamentoDeMes(isFechamentoDeMes));
    if (modo === MODO_APP_SERIES) {
      const periodo = configuracaoGlobalRelatorio.periodo;
      const isPeriodoDatasEspecificas =
        periodo === ID_PERIODO_DATAS_ESPECIFICAS_MODO_SERIES;
      yield put(
        selecionarPeriodicidade(configuracaoGlobalRelatorio.periodicidade)
      );
      yield put(selecionarAjusteDias(configuracaoGlobalRelatorio.ajusteDias));
      yield put(selecionarPeriodo([periodo]));
      if (isPeriodoDatasEspecificas) {
        yield put(
          selecionarDataInicial(
            Moment(configuracaoGlobalRelatorio.dataInicial, FORMATO_DATA_BANCO)
          )
        );
        yield put(
          selecionarDataFinal(
            Moment(configuracaoGlobalRelatorio.dataFinal, FORMATO_DATA_BANCO)
          )
        );
      }
      yield put(
        selecionarExibirDatasSemNegociacaoSeries(
          configuracaoGlobalRelatorio.exibirDatasSemNegociacao
        )
      );
      for (let id of configuracaoGlobalRelatorio.identificadoresAtivos) {
        yield put(selecionarIdentificadorSeries(id));
      }
    }
    if (visualizacao) {
      yield put(restoreGridState(parseVisualization(visualizacao)));
    } else {
      yield put(resetGridState());
    }
    yield put(abrirArquivoSuccess());
  } catch (error) {
    const { translate } = yield select(i18n);
    yield put(erroAoAbrirArquivo());
    yield frameApi.salvarArquivoMedidas(null);
    yield frameApi.sendNotificationWithTemplate(
      translate("configuracaoRelatorio.erroAbrirArquivo"),
      "error"
    );
  }
}

export function* abrirArquivo({ api, frameApi }) {
  yield takeLatest("ABRIR_ARQUIVO", function*(action): any {
    if (action.sobrescreverMedidas) {
      if (typeof action.arquivo === "string") {
        const arquivo = yield api.unbatchedClient.get(
          `/configuracaorelatorio/abrirArquivo?nome=${encodeURIComponent(
            action.arquivo
          )}`
        );
        yield put(abrirArquivoAction(arquivo));
      } else {
        yield fork(function*() {
          const { sucesso } = yield race({
            sucesso: take("ABRIR_ARQUIVO_SUCCESS"),
            erro: take("ERRO_AO_ABRIR_ARQUIVO"),
          });
          if (sucesso && action.gerarRelatorio) {
            yield put(gerarRelatorio());
          }
        });
        yield abrir({ arquivo: action.arquivo, api, frameApi });
      }
    } else {
      yield put(cancelaAberturaDeArquivo());
    }
  });
}

export function* abrirTemplate({ api, frameApi }) {
  yield takeLatest("ABRIR_TEMPLATE", function*(action): any {
    yield fork(function*() {
      const { sucesso } = yield race({
        sucesso: take("ABRIR_ARQUIVO_SUCCESS"),
        erro: take("ERRO_AO_ABRIR_ARQUIVO"),
      });
      if (sucesso) yield put(gerarRelatorio());
    });
    yield abrir({ arquivo: action.arquivo, api, frameApi });
  });
}

export function* salvarArquivo({ api, frameApi, history }) {
  yield takeLatest("SALVAR_ARQUIVO", function*(action): any {
    const { translate } = yield select(i18n);
    try {
      const medidasSelecionadas: Array<MedidaSelecionada> = yield select(
        getMedidasSelecionadas
      );
      const visualization = yield select(getGridState);
      const configuracaoGlobalRelatorio = yield select(
        getConfiguracaoGlobalRelatorio
      );

      const medidasParaPersonalizar: Array<MedidaParaPersonalizar> = yield select(
        getMedidasParaPersonalizar
      );

      const arquivoSalvo = yield api.salvarArquivoMedidas(
        action.arquivo,
        medidasSelecionadas,
        configuracaoGlobalRelatorio,
        visualization,
        medidasParaPersonalizar
      );

      yield all([
        put(fetchRemoteData("arquivosMedidas")),
        put(abrirArquivoAction(arquivoSalvo)),
        take("ABRIR_ARQUIVO_SUCCESS"),
      ]);

      yield put(arquivoSalvoAction(arquivoSalvo));
      yield frameApi.sendNotificationWithTemplate(
        translate("telas.salvoComSucesso", [arquivoSalvo.nome]),
        "success"
      );

      history.goBack();
    } catch (error) {
      yield put(erroAoSalvarArquivo());
      yield frameApi.sendNotificationWithTemplate(
        translate("configuracaoRelatorio.erroSalvarArquivo"),
        "error"
      );
    }
  });
}

export function* excluirArquivo({ api, frameApi }) {
  yield takeEvery("EXCLUIR_ARQUIVO", function*(action): any {
    const { translate } = yield select(i18n);
    try {
      yield api.excluirArquivoMedidas(action.arquivo);

      yield frameApi.sendNotificationWithTemplate(
        translate("telas.excluidoComSucesso", [action.arquivo.nome]),
        "success"
      );

      yield put(fetchRemoteData("arquivosMedidas"));
    } catch (error) {
      yield frameApi.sendNotificationWithTemplate(
        translate("configuracaoRelatorio.erroExcluirArquivo", [
          action.arquivo.nome,
        ]),
        "error"
      );
    }
  });
}

export default function* arquivosMedidasSaga({ api, frameApi, history }) {
  yield fork(obterListaArquivos, { api });
  yield fork(obterGruposCompartilhamento, { api });
  yield fork(obterListaTemplates, { api });
  yield fork(compartilharArquivo, { api, frameApi });
  yield fork(abrirArquivo, { api, frameApi });
  yield fork(abrirTemplate, { api, frameApi });
  yield fork(salvarArquivo, { api, frameApi, history });
  yield fork(excluirArquivo, { api, frameApi });
}
